import { isEmpty } from 'lodash'
import {
  startLoading,
  stopLoading,
} from '../../../common/modules/loading/actions'
import { LoadingTypes } from '../../../common/modules/loading/reducer'
import { AxiosError, AxiosResponse } from 'axios'
import { AppDispatch, RootState } from '../../../store'
import {
  fetchPartnerIqTableData,
  fetchPartnerIqOverviewData,
  putPartnerIqIcpFormData,
  fetchPartnerIqIcpFormData,
  fetchPartnerIqProfileData,
  fetchPartnerIqOpportunitiesReport,
  fetchPartnerIqTableReport,
  fetchPartnerIqInvitationDetails,
  fetchPartnerIqSegments,
  fetchCollabRoomPageData,
  fetchPartnerIqMetrics as fetchPartnerIqMetricsApi,
  saveCollabRoomPageData,
  createCollabRoomPageData,
} from '../../api/PartnerIq'
import type {
  OverviewComponentData,
  PartnerIqTableData,
  PartnerIqOverviewResponse,
  ICPData,
  ProfileData,
  SegmentsData,
  AdminPanelPageData,
  PartnerIqInvitationDetails,
} from './reducer'
import { camelize, snakeize } from 'casing'
import {
  partnerIQPageDataEmpty,
  collateralTabs,
  PARTNER_ORIGINATED_OPPORTUNITIES_LABEL,
  type CollateralItem,
  type CollateralTabId,
} from '../../../mocks/partneriq/mockData'
import { updateAppAlert } from '../../../common/modules/appAlert/actions'
import { getErrorMessages } from '../../../common/utils/error'
import { ensureAbsoluteUrl } from '../../../common/utils/ensureAbsoluteUrl'
import {
  ICPCalculationInProgressMessage,
  ICPCreateSuccessMessage,
  ICPUpdateSuccessMessage,
  RequestFailureMessage,
} from '../../../common/utils/messagesContants'
import { errorLogger } from '../../../common/utils/errorLogger'
import FileDownload from 'js-file-download'
import { v4 as uuidv4 } from 'uuid'

export enum PartnerIQActionTypes {
  SET_PARTNER_IQ_TABLE_DATA = 'SET_PARTNER_IQ_TABLE_DATA',
  SET_PARTNER_IQ_OVERVIEW_DATA = 'SET_PARTNER_IQ_OVERVIEW_DATA',
  CLEAR_PARTNER_IQ_TABLE_DATA = 'CLEAR_PARTNER_IQ_TABLE_DATA',
  SET_PARTNER_IQ_PROFILE_DATA = 'SET_PARTNER_IQ_PROFILE_DATA',
  CLEAR_PARTNER_IQ_PROFILE_DATA = 'CLEAR_PARTNER_IQ_PROFILE_DATA',
  SET_PARTNER_IQ_ICP_FORM_DATA = 'SET_PARTNER_IQ_ICP_FORM_DATA',
  SET_PARTNER_IQ_SEGMENTS_DATA = 'SET_PARTNER_IQ_SEGMENTS_DATA',
  CLEAR_PARTNER_IQ_SEGMENTS_DATA = 'CLEAR_PARTNER_IQ_SEGMENTS_DATA',
  SET_PARTNER_IQ_INVITATION_DETAILS = 'SET_PARTNER_IQ_INVITATION_DETAILS',
  CLEAR_PARTNER_IQ_INVITATION_DETAILS = 'CLEAR_PARTNER_IQ_INVITATION_DETAILS',
  SET_ADMIN_PANEL_PAGE_DATA = 'SET_ADMIN_PANEL_PAGE_DATA',
  SET_ADMIN_PANEL_SECTION = 'SET_ADMIN_PANEL_SECTION',
  SET_ADMIN_PANEL_EDIT_MODE = 'SET_ADMIN_PANEL_EDIT_MODE',
  SET_ADMIN_PANEL_EDITING_SECTION = 'SET_ADMIN_PANEL_EDITING_SECTION',
  SET_ADMIN_PANEL_FORM_VALUE = 'SET_ADMIN_PANEL_FORM_VALUE',
  SET_ADMIN_PANEL_COLLATERAL_ACTIVE_TAB = 'SET_ADMIN_PANEL_COLLATERAL_ACTIVE_TAB',
  CLEAR_ADMIN_PANEL = 'CLEAR_ADMIN_PANEL',
}

export const setPartnerIqOverviewData = (data: OverviewComponentData[]) => ({
  type: PartnerIQActionTypes.SET_PARTNER_IQ_OVERVIEW_DATA,
  payload: data,
})

export const setPartnerIqTableData = (data: PartnerIqTableData) => ({
  type: PartnerIQActionTypes.SET_PARTNER_IQ_TABLE_DATA,
  payload: data,
})

export const clearPartnerIqTableData = () => ({
  type: PartnerIQActionTypes.CLEAR_PARTNER_IQ_TABLE_DATA,
})

export const setPartnerIqProfileData = (data: ProfileData) => ({
  type: PartnerIQActionTypes.SET_PARTNER_IQ_PROFILE_DATA,
  payload: data,
})

export const clearPartnerIqProfileData = () => ({
  type: PartnerIQActionTypes.CLEAR_PARTNER_IQ_PROFILE_DATA,
})

export const setPartnerIqIcpFormData = (data: ICPData | null) => ({
  type: PartnerIQActionTypes.SET_PARTNER_IQ_ICP_FORM_DATA,
  payload: data,
})

export const setPartnerIqSegmentsData = (data: SegmentsData) => ({
  type: PartnerIQActionTypes.SET_PARTNER_IQ_SEGMENTS_DATA,
  payload: data,
})

export const clearPartnerIqSegmentsData = () => ({
  type: PartnerIQActionTypes.CLEAR_PARTNER_IQ_SEGMENTS_DATA,
})

export const setPartnerIqInvitationDetails = (
  details: PartnerIqInvitationDetails
) => ({
  type: PartnerIQActionTypes.SET_PARTNER_IQ_INVITATION_DETAILS,
  payload: details,
})

export const clearPartnerIqInvitationDetails = () => ({
  type: PartnerIQActionTypes.CLEAR_PARTNER_IQ_INVITATION_DETAILS,
})

export const setAdminPanelPageData = (data: AdminPanelPageData) => ({
  type: PartnerIQActionTypes.SET_ADMIN_PANEL_PAGE_DATA,
  payload: data,
})

export const setAdminPanelSection =
  <K extends keyof AdminPanelPageData>(
    section: K,
    value:
      | AdminPanelPageData[K]
      | ((prev: AdminPanelPageData[K]) => AdminPanelPageData[K])
  ) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState()
    const prev = state.partnerIQ.adminPanelPageData
    if (!prev) return
    const resolvedValue =
      typeof value === 'function'
        ? (value as (p: AdminPanelPageData[K]) => AdminPanelPageData[K])(
            prev[section]
          )
        : value
    dispatch({
      type: PartnerIQActionTypes.SET_ADMIN_PANEL_SECTION,
      payload: { section, value: resolvedValue },
    })
  }

export const setAdminPanelEditMode = (isEditMode: boolean) => ({
  type: PartnerIQActionTypes.SET_ADMIN_PANEL_EDIT_MODE,
  payload: isEditMode,
})

export const setAdminPanelEditingSection = (sectionId: string | null) => ({
  type: PartnerIQActionTypes.SET_ADMIN_PANEL_EDITING_SECTION,
  payload: sectionId,
})

export const setAdminPanelFormValue = (formValue: string) => ({
  type: PartnerIQActionTypes.SET_ADMIN_PANEL_FORM_VALUE,
  payload: formValue,
})

export const setAdminPanelCollateralActiveTab = (tabId: CollateralTabId) => ({
  type: PartnerIQActionTypes.SET_ADMIN_PANEL_COLLATERAL_ACTIVE_TAB,
  payload: tabId,
})

export const clearAdminPanel = () => ({
  type: PartnerIQActionTypes.CLEAR_ADMIN_PANEL,
})

/** Response is valid partner-info data (not an HTML error page / redirect).
 *  Accepts both API key names (company_details) and app key names (companyInfo). */
const isPartnerInfoPageData = (data: unknown): boolean => {
  if (!data || typeof data !== 'object') return false
  const d = data as Record<string, unknown>
  return (
    'companyDetails' in d ||
    'company_details' in d ||
    'companyInfo' in d ||
    'company_info' in d ||
    'trustedPartners' in d ||
    'trusted_partners' in d ||
    'trustedBy' in d ||
    'trusted_by' in d ||
    'highlights' in d ||
    'teams' in d ||
    'partner_id' in d
  )
}

/** API uses snake_case; app uses camelCase. We read both key formats from API; snakeize payloads when saving. */

const COLLAB_ROOM_API_KEYS = {
  companyDetails: 'company_details',
  trustedPartners: 'trusted_partners',
  highlights: 'highlights',
  teams: 'teams',
  collateral: 'collateral',
  solutions: 'solutions',
  createdBy: 'created_by',
  partnerId: 'partner_id',
} as const

/** Unwrap API response: backend may return { partner_info: { ... } } or flat { company_details, ... }. Returns raw (no camelize) to avoid casing issues. */
function unwrapPartnerInfoResponse(
  raw: Record<string, unknown>
): Record<string, unknown> {
  const inner = raw.partnerInfo ?? raw.partner_info
  if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as Record<string, unknown>
  }
  return raw
}

/** Safe payload for camelize: backend may return 200 with HTML or non-object body. */
function getPartnerInfoPayload(
  response: { data?: unknown } | null
): Record<string, unknown> {
  const d = response?.data
  if (d != null && typeof d === 'object' && !Array.isArray(d)) {
    return d as Record<string, unknown>
  }
  return {}
}

export type GetCollabRoomPageDataOptions = {
  /** When API returns empty teams, use this to preserve recently saved alliance/sales data. */
  fallbackAllianceAndSales?: AdminPanelPageData['allianceAndSales']
  /** When API returns empty/missing solution items after save, keep the list we persisted (incl. per-solution is_hidden). */
  fallbackSolutions?: AdminPanelPageData['solutions']
  /** When API omits section_hidden, preserve solutions panel visibility after save. */
  fallbackSolutionsSectionHidden?: boolean
}

export const getCollabRoomPageData =
  (partnerId: string, options?: GetCollabRoomPageDataOptions) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(
      startLoading(LoadingTypes.FETCH_PARTNER_IQ_COLLAB_ROOM_PAGE_DATA)
    )
    try {
      const response = await fetchCollabRoomPageData(partnerId)
      const payload = getPartnerInfoPayload(response ?? null)
      const raw = payload as Record<string, unknown>
      const data = unwrapPartnerInfoResponse(raw)

      if (isEmpty(payload)) {
        await dispatch(setAdminPanelPageData(partnerIQPageDataEmpty))
        return
      }

      // Guard: must look like a partner-info object, not an HTML error page
      if (!isPartnerInfoPageData(data)) {
        await dispatch(setAdminPanelPageData(partnerIQPageDataEmpty))
        return
      }

      let merged = buildMergedPageData(data)
      const fallback = options?.fallbackAllianceAndSales
      if (
        fallback &&
        Array.isArray(fallback) &&
        fallback.length > 0 &&
        (!Array.isArray(merged.allianceAndSales) ||
          merged.allianceAndSales.length === 0)
      ) {
        merged = { ...merged, allianceAndSales: fallback }
      }
      /** Post-save refetch passes `fallbackSolutions` (save thunk only). Prefer it so UI
       *  keeps order and per-item `is_hidden` when GET returns a non-empty list in wrong order. */
      const fallbackSolutions = options?.fallbackSolutions
      if (
        fallbackSolutions &&
        Array.isArray(fallbackSolutions) &&
        fallbackSolutions.length > 0
      ) {
        merged = { ...merged, solutions: fallbackSolutions }
      }
      if (
        options?.fallbackSolutionsSectionHidden === true &&
        merged.solutionsSectionHidden !== true
      ) {
        merged = { ...merged, solutionsSectionHidden: true }
      }
      await dispatch(setAdminPanelPageData(merged))
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      try {
        const globalState = getState()
        errorLogger({ globalState })(
          error as Error,
          errorResponse?.data?.track_id
        )
      } catch (_logError) {
        // avoid rethrowing so the UI always recovers
      }
      // Don't overwrite existing data on fetch error – preserve admin panel edits
      const current = getState().partnerIQ.adminPanelPageData
      if (!current) {
        await dispatch(setAdminPanelPageData(partnerIQPageDataEmpty))
      }
    } finally {
      await dispatch(
        stopLoading(LoadingTypes.FETCH_PARTNER_IQ_COLLAB_ROOM_PAGE_DATA)
      )
    }
  }

export type MetricItem = { label: string; value: string }

/** Unwrap the `/partner_info/metrics` response into a flat array of metric items. */
function extractMetricsArray(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []
  const d = data as Record<string, unknown>
  const inner = d.partner_info ?? d.partnerInfo
  const obj =
    inner && typeof inner === 'object' && !Array.isArray(inner)
      ? (inner as Record<string, unknown>)
      : d
  const cd = obj.company_details ?? obj.companyDetails
  if (cd && typeof cd === 'object' && !Array.isArray(cd)) {
    const nested =
      (cd as Record<string, unknown>).info ??
      (cd as Record<string, unknown>).metrics
    if (Array.isArray(nested)) return nested
  }
  const flat = obj.info ?? obj.metrics
  if (Array.isArray(flat)) return flat
  return []
}

/** Fetch CRM-synced company info metrics. Returns a normalized array of
 *  `{ label, value }` items for the component to merge into its local form state,
 *  or `null` when the fetch fails / returns empty data. */
export const getCompanyInfoMetrics =
  (partnerId: string) =>
  async (
    dispatch: AppDispatch,
    getState: () => RootState
  ): Promise<MetricItem[] | null> => {
    await dispatch(startLoading(LoadingTypes.FETCH_PARTNER_IQ_METRICS))
    try {
      const response = await fetchPartnerIqMetricsApi(partnerId)
      const raw = extractMetricsArray(response?.data)
      if (raw.length === 0) return null
      return raw.map(m => ({
        label: String(m.title ?? m.label ?? m.name ?? ''),
        value: String(m.value ?? ''),
      }))
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      try {
        const globalState = getState()
        errorLogger({ globalState })(
          error as Error,
          errorResponse?.data?.track_id
        )
      } catch (_logError) {
        // avoid rethrowing so the UI always recovers
      }
      return null
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_PARTNER_IQ_METRICS))
    }
  }

export const getPartnerIqInvitationDetails =
  (partnerId: string, contactId: string, signal?: AbortSignal) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(clearPartnerIqInvitationDetails())
    try {
      const response = await fetchPartnerIqInvitationDetails(
        partnerId,
        contactId,
        signal
      )
      const details = camelize(response?.data || {}) as {
        collabInvitation?: {
          createdDate?: string
        }
        invitationTimestamp?: string
        lastVisitedTimestamp?: string
      }
      dispatch(
        setPartnerIqInvitationDetails({
          invitationTimestamp:
            details.collabInvitation?.createdDate ||
            details.invitationTimestamp,
          lastVisitedTimestamp: details.lastVisitedTimestamp,
        })
      )
    } catch {}
  }

/** Get value by camelCase or snake_case key (API may return either). */
function getByKey<T>(
  obj: Record<string, unknown>,
  camel: string,
  snake: string
): T | undefined {
  const v = obj[camel] ?? obj[snake]
  return v as T | undefined
}

/** Build app page data from API response. Reads both snake_case and camelCase keys. */
function buildMergedPageData(raw: Record<string, unknown>): AdminPanelPageData {
  const d = partnerIQPageDataEmpty
  try {
    const companyDetails = getByKey<unknown>(
      raw,
      'companyDetails',
      'company_details'
    )
    const trustedPartners = getByKey<unknown>(
      raw,
      'trustedPartners',
      'trusted_partners'
    )
    const highlights = getByKey<unknown>(raw, 'highlights', 'highlights')
    const teams = getByKey<unknown>(raw, 'teams', 'teams')
    const collateral = getByKey<unknown>(raw, 'collateral', 'collateral')
    const solutions = getByKey<unknown>(raw, 'solutions', 'solutions')

    const companyInfo = safeCall(
      () => buildCompanyInfoFromApi(companyDetails, d.companyInfo),
      d.companyInfo
    )
    const trustedBy = safeCall(
      () => buildTrustedByFromApi(trustedPartners ?? raw, d.trustedBy),
      d.trustedBy
    )
    const highlightMetricsBase = safeCall(
      () => buildHighlightMetricsFromApi(highlights, d.highlightMetrics),
      d.highlightMetrics
    )
    const highlightMetrics = {
      ...highlightMetricsBase,
      /** Header label: backend puts name on company_details, not highlights */
      companyName:
        String(companyInfo.companyName ?? '').trim() ||
        String(highlightMetricsBase.companyName ?? '').trim(),
    }
    const allianceAndSalesBase = safeCall(
      () => normalizeAllianceAndSales(teams, d.allianceAndSales),
      d.allianceAndSales
    )
    const allianceAndSales = applyCompanyNameToAllianceSalesTeamTitles(
      allianceAndSalesBase,
      companyInfo.companyName ?? ''
    )
    const collateralData = safeCall(
      () => buildCollateralFromApi(collateral, d.collateral),
      d.collateral
    )
    const solutionsData = safeCall(() => {
      const s = buildSolutionsFromApi(solutions)
      return s && s.length > 0 ? s : d.solutions
    }, d.solutions)
    const teamsRaw = getByKey<Record<string, unknown>>(raw, 'teams', 'teams')
    const teamsVisibility =
      teamsRaw && typeof teamsRaw === 'object'
        ? {
            allianceSectionHidden:
              teamsRaw.allianceSectionHidden === true ||
              teamsRaw.alliance_section_hidden === true,
            salesSectionHidden:
              teamsRaw.salesSectionHidden === true ||
              teamsRaw.sales_section_hidden === true,
          }
        : undefined
    const collateralRaw = collateral as Record<string, unknown> | undefined
    const collateralWithVisibility =
      collateralData && collateralRaw
        ? {
            ...collateralData,
            sectionHidden:
              collateralRaw.sectionHidden === true ||
              collateralRaw.section_hidden === true,
            firstCallSectionHidden:
              collateralRaw.firstCallSectionHidden === true ||
              collateralRaw.first_call_section_hidden === true,
            solutionBriefsSectionHidden:
              collateralRaw.solutionBriefsSectionHidden === true ||
              collateralRaw.solution_brief_section_hidden === true,
            winWiresSectionHidden:
              collateralRaw.winWiresSectionHidden === true ||
              collateralRaw.win_wire_section_hidden === true,
          }
        : collateralData
    const solutionsRaw = solutions as Record<string, unknown> | undefined
    const solutionsSectionHidden =
      solutionsRaw &&
      (solutionsRaw.sectionHidden === true ||
        solutionsRaw.section_hidden === true)
    return {
      companyInfo,
      trustedBy,
      highlightMetrics,
      allianceAndSales,
      teamsVisibility,
      collateral: collateralWithVisibility ?? collateralData,
      solutions: solutionsData,
      solutionsSectionHidden: solutionsSectionHidden || undefined,
    }
  } catch {
    return d
  }
}

function safeCall<T>(fn: () => T, fallback: T): T {
  try {
    return fn()
  } catch {
    return fallback
  }
}

const COMPANY_INFO_DEFAULT_STAT_LABELS = [
  'Total Co-sell revenue',
  'AWS originated opportunities',
  PARTNER_ORIGINATED_OPPORTUNITIES_LABEL,
]

/** Build company info from API. Backend: company_name, company_description, linkedin_url, website, info, section_hidden. */
function buildCompanyInfoFromApi(
  raw: unknown,
  defaultVal: AdminPanelPageData['companyInfo']
): AdminPanelPageData['companyInfo'] {
  if (raw == null || typeof raw !== 'object') return defaultVal
  const api = raw as Record<string, unknown>
  const companyName = (api.companyName ??
    api.company_name ??
    defaultVal.companyName) as string
  const description = (api.description ??
    api.company_description ??
    defaultVal.description) as string
  const linkedinUrl = (api.linkedinUrl ??
    api.linkedin_url ??
    defaultVal.linkedinUrl) as string
  const website = api.website ?? api.websiteUrl ?? api.website_url
  const websiteUrl = (
    website != null ? String(website) : defaultVal.websiteUrl
  ) as string
  const websiteLabel = (api.websiteLabel ??
    api.website_label ??
    defaultVal.websiteLabel) as string
  let stats = defaultVal.stats
  const info = api.info
  if (Array.isArray(info) && info.length > 0) {
    const isCompanyOriginatedStat = (l: string) =>
      l.endsWith(' originated opportunities') &&
      l !== 'AWS originated opportunities'
    stats = info.map((s: unknown, idx: number) => {
      const item = s as Record<string, unknown>
      const rawLabel = String(item.title ?? item.label ?? item.name ?? '')
      const useCompanyName =
        isCompanyOriginatedStat(rawLabel) || (idx === 2 && !rawLabel)
      const label = useCompanyName
        ? PARTNER_ORIGINATED_OPPORTUNITIES_LABEL
        : rawLabel ||
          (idx < COMPANY_INFO_DEFAULT_STAT_LABELS.length
            ? COMPANY_INFO_DEFAULT_STAT_LABELS[idx]
            : '')
      return {
        label,
        value: String(item.value ?? ''),
        showGradient:
          idx === 0
            ? true
            : item.showGradient === true || item.show_gradient === true,
        isVisible:
          item.isHidden === true || item.is_hidden === true ? false : true,
      }
    })
  } else if (stats.length === 0) {
    stats = COMPANY_INFO_DEFAULT_STAT_LABELS.map((label, idx) => ({
      label: idx === 2 ? PARTNER_ORIGINATED_OPPORTUNITIES_LABEL : label,
      value: '',
      showGradient: idx === 0,
      isVisible: true,
    }))
  }
  const sectionHidden =
    api.sectionHidden === true || api.section_hidden === true
  const companyLogo = (api.companyLogo ??
    api.company_logo ??
    defaultVal.companyLogo) as string | undefined
  return {
    ...defaultVal,
    companyName: String(companyName ?? ''),
    description: String(description ?? ''),
    linkedinUrl: String(linkedinUrl ?? ''),
    websiteUrl: String(websiteUrl ?? ''),
    websiteLabel: String(websiteLabel ?? ''),
    stats,
    sectionHidden: sectionHidden || undefined,
    companyLogo: companyLogo ? String(companyLogo) : undefined,
  }
}

/** Extract logo/image URL from an API item that may use different keys or nested shape. */
function pickLogoUrl(c: Record<string, unknown>): string {
  const raw =
    c.customer_logo_url ??
    c.customerLogoUrl ??
    c.logo_url ??
    c.logoUrl ??
    c.url ??
    c.logo ??
    c.image_url ??
    c.imageUrl ??
    c.author_logo_url
  if (typeof raw === 'string' && raw.trim()) return raw.trim()
  if (
    raw != null &&
    typeof raw === 'object' &&
    !Array.isArray(raw) &&
    'url' in raw
  ) {
    const u = (raw as { url?: string }).url
    if (typeof u === 'string' && u.trim()) return u.trim()
  }
  return ''
}

/** Build trusted by from API. Backend: user_logos, testimonials (review, author_name, designation, author_logo_url, author_company_url), section_hidden. */
function buildTrustedByFromApi(
  raw: unknown,
  defaultVal: AdminPanelPageData['trustedBy']
): AdminPanelPageData['trustedBy'] {
  if (raw == null || typeof raw !== 'object') return defaultVal
  const api = raw as Record<string, unknown>
  const sectionHidden =
    api.sectionHidden === true || api.section_hidden === true
  const logosRaw =
    api.userLogos ?? api.user_logos ?? api.customerLogos ?? api.customer_logos
  const customerLogos = Array.isArray(logosRaw)
    ? logosRaw
        .filter(
          (c): c is Record<string, unknown> =>
            c != null && typeof c === 'object'
        )
        .map((c, i) => ({
          id: String(c.id ?? `logo-${i}`),
          url: pickLogoUrl(c),
          name: c.name != null ? String(c.name) : undefined,
          website:
            c.customer_website != null ? String(c.customer_website) : undefined,
          isHidden: c.isHidden === true || c.is_hidden === true,
        }))
    : []
  const testimonialsRaw = api.testimonials
  const testimonials = Array.isArray(testimonialsRaw)
    ? testimonialsRaw
        .filter(
          (t): t is Record<string, unknown> =>
            t != null && typeof t === 'object'
        )
        .map((t, i) => ({
          id: String(t.id ?? `t-${i}`),
          quote: String(t.quote ?? t.review ?? ''),
          name: String(t.name ?? t.author_name ?? ''),
          title: String(t.title ?? t.designation ?? ''),
          company: String(
            t.company ?? t.author_company_url ?? t.author_company ?? ''
          ),
          companyLogo: (() => {
            const v =
              t.companyLogo ?? t.company_logo ?? t.author_company_logo_url
            if (v != null) return String(v)
            // Fallback: author_company_url may contain company logo URL if saved incorrectly
            const authorCompanyUrl =
              t.author_company_url ??
              (t as { authorCompanyUrl?: string }).authorCompanyUrl
            if (
              typeof authorCompanyUrl === 'string' &&
              authorCompanyUrl.trim()
            ) {
              const url = authorCompanyUrl.trim()
              if (
                /\.(jpg|jpeg|png|webp|gif)$/i.test(url) ||
                url.includes('trusted_by_testimonial')
              ) {
                return url
              }
            }
            return undefined
          })(),
          profilePicture: (() => {
            const v = t.profilePicture ?? t.profile_picture ?? t.author_logo_url
            return v != null ? String(v) : undefined
          })(),
          isHidden: t.isHidden === true || t.is_hidden === true,
        }))
    : []
  return {
    ...defaultVal,
    customerLogos,
    testimonials,
    sectionHidden: sectionHidden || undefined,
  }
}

/** Labels for the 5 fixed AWS metrics – used to split API information_badges into awsMetrics vs custom badges. */
const AWS_METRIC_LABELS = [
  'AWS Competencies',
  'Service Validations',
  'Partner Programs',
  'AWS Certifications',
  'Launches',
]

/** Build highlight metrics from API. Backend: highlight_logo_url, partner_id, information_badges, section_hidden.
 *  Splits information_badges into awsMetrics (first 5 by label match) and informationBadges (rest) to avoid duplicates on save. */
function buildHighlightMetricsFromApi(
  raw: unknown,
  defaultVal: AdminPanelPageData['highlightMetrics']
): AdminPanelPageData['highlightMetrics'] {
  if (raw == null || typeof raw !== 'object') return defaultVal
  const api = raw as Record<string, unknown>

  const highlightLogoUrl = (api.highlight_logo_url ?? api.highlightLogoUrl) as
    | string
    | null
  const recognitionBadgeFileLinks = highlightLogoUrl
    ? [highlightLogoUrl]
    : (defaultVal.recognitionBadgeFileLinks ?? [])

  const partnerId = (api.partner_id ??
    api.partnerId ??
    defaultVal.partnerId) as string

  const rawBadges = api.information_badges ?? api.informationBadges
  const badges = Array.isArray(rawBadges)
    ? rawBadges.map((b: Record<string, unknown>, i: number) => ({
        id: String(b.id ?? `info-badge-${i}`),
        label: String(b.label ?? b.title ?? b.name ?? ''),
        description: String(b.description ?? ''),
        value: String(b.value ?? ''),
        isVisible: b.is_hidden !== true && b.isHidden !== true,
      }))
    : []

  const awsMetrics = AWS_METRIC_LABELS.map((defLabel, idx) => {
    const fromApi = badges.find((b: { label: string }) => b.label === defLabel)
    if (fromApi) {
      return {
        id: fromApi.id,
        label: defLabel,
        description: fromApi.description,
        value: fromApi.value,
        isVisible: fromApi.isVisible,
      }
    }
    const defaultAws = (defaultVal.awsMetrics ?? [])[idx]
    return (
      defaultAws ?? {
        id: `aws-${idx}`,
        label: defLabel,
        description: '',
        value: '',
        isVisible: true,
      }
    )
  })

  const informationBadges = badges.filter(
    (b: { label: string }) => !AWS_METRIC_LABELS.includes(b.label)
  )

  const sectionHidden =
    api.section_hidden === true || api.sectionHidden === true

  const companyNameFromHighlights = String(
    (api.company_name ?? api.companyName ?? '') as string
  ).trim()

  return {
    ...defaultVal,
    companyName: companyNameFromHighlights || defaultVal.companyName,
    recognitionBadgeFileLinks,
    partnerId,
    awsMetrics,
    informationBadges,
    sectionHidden: sectionHidden || undefined,
  }
}

function mergeSectionWithDefault<T extends Record<string, unknown>>(
  defaultSection: T,
  apiSection: unknown
): T {
  if (
    apiSection == null ||
    typeof apiSection !== 'object' ||
    Array.isArray(apiSection)
  ) {
    return defaultSection
  }
  const api = apiSection as Record<string, unknown>
  const result = { ...defaultSection } as Record<string, unknown>
  for (const key of Object.keys(defaultSection)) {
    const defaultVal = defaultSection[key]
    const apiVal = api[key] ?? api[snakeize(key)]
    if (apiVal !== undefined && apiVal !== null) {
      if (Array.isArray(defaultVal)) {
        result[key] = Array.isArray(apiVal) ? apiVal : defaultVal
      } else if (
        typeof defaultVal === 'object' &&
        defaultVal !== null &&
        !Array.isArray(defaultVal) &&
        typeof apiVal === 'object' &&
        apiVal !== null &&
        !Array.isArray(apiVal)
      ) {
        result[key] = mergeSectionWithDefault(
          defaultVal as Record<string, unknown>,
          apiVal
        )
      } else {
        result[key] = apiVal
      }
    }
  }
  return result as T
}

/** Match mock/API generic labels — company name lives on company_details, not teams. */
function applyCompanyNameToAllianceSalesTeamTitles(
  teams: AdminPanelPageData['allianceAndSales'],
  companyName: string
): AdminPanelPageData['allianceAndSales'] {
  const cn = String(companyName ?? '').trim()
  if (!cn || !Array.isArray(teams)) return teams
  return teams.map(team => {
    const t = String(team.teamName ?? '').trim()
    const lower = t.toLowerCase()
    if (lower === 'alliance team') {
      return { ...team, teamName: `${cn} alliance team` }
    }
    if (lower === 'sales team') {
      return { ...team, teamName: `${cn} sales team` }
    }
    return team
  })
}

function normalizeAllianceAndSales(
  raw: unknown,
  defaultVal: AdminPanelPageData['allianceAndSales']
): AdminPanelPageData['allianceAndSales'] {
  let arr: unknown[] = []
  if (Array.isArray(raw)) {
    arr = raw
  } else if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
    const obj = raw as Record<string, unknown>
    const teamsFromKey = obj.teams ?? obj.members
    if (Array.isArray(teamsFromKey) && teamsFromKey.length > 0) {
      const first = teamsFromKey[0]
      const isTeamObject =
        first != null &&
        typeof first === 'object' &&
        !Array.isArray(first) &&
        (typeof (first as Record<string, unknown>).team_name === 'string' ||
          typeof (first as Record<string, unknown>).teamName === 'string' ||
          Array.isArray((first as Record<string, unknown>).members))
      if (isTeamObject) {
        arr = teamsFromKey
      } else if (
        'allianceSectionHidden' in obj ||
        'alliance_section_hidden' in obj ||
        'salesSectionHidden' in obj ||
        'sales_section_hidden' in obj
      ) {
        arr = teamsFromKey
      }
    }
    if (arr.length === 0 && 'members' in obj) {
      const members = obj.members
      if (
        Array.isArray(members) &&
        members.length > 0 &&
        ('allianceSectionHidden' in obj ||
          'alliance_section_hidden' in obj ||
          'salesSectionHidden' in obj ||
          'sales_section_hidden' in obj)
      ) {
        arr = members
      }
    }
    if (arr.length === 0) {
      arr = Object.values(obj)
    }
  }
  const validTeams = arr.filter(
    (t): t is Record<string, unknown> =>
      t != null &&
      typeof t === 'object' &&
      !Array.isArray(t) &&
      (typeof (t as Record<string, unknown>).teamName === 'string' ||
        typeof (t as Record<string, unknown>).team_name === 'string' ||
        Array.isArray((t as Record<string, unknown>).members))
  )
  if (validTeams.length === 0) {
    const flatMembers = arr.filter((m): m is Record<string, unknown> => {
      if (m == null || typeof m !== 'object' || Array.isArray(m)) {
        return false
      }
      const r = m as Record<string, unknown>
      const email = r.email
      const hasEmail =
        typeof email === 'string' && String(email).trim().length > 0
      const imageUrl = r.image_url ?? r.profilePicture ?? r.profile_picture
      const hasImageUrl =
        typeof imageUrl === 'string' && String(imageUrl).trim().length > 0
      return (
        typeof r.name === 'string' ||
        typeof r.role === 'string' ||
        typeof r.title === 'string' ||
        hasEmail ||
        typeof r.team === 'string' ||
        hasImageUrl
      )
    }) as Record<string, unknown>[]
    if (flatMembers.length > 0) {
      const byTeam = new Map<string, Record<string, unknown>[]>()
      for (const m of flatMembers) {
        const teamVal = String(
          m.team ?? m.team_name ?? m.teamName ?? 'alliance'
        )
        const teamName = teamVal === 'sales' ? 'Sales team' : 'Alliance team'
        if (!byTeam.has(teamName)) byTeam.set(teamName, [])
        byTeam.get(teamName)!.push(m)
      }
      const teamOrder = ['Alliance team', 'Sales team']
      const entries = Array.from(byTeam.entries()).sort(
        (a, b) => teamOrder.indexOf(a[0]) - teamOrder.indexOf(b[0])
      )
      return entries.map(([teamName, members], teamIdx) => ({
        teamName,
        members: members.map((row, i) => mapApiMemberToApp(row, teamIdx, i)),
      })) as AdminPanelPageData['allianceAndSales']
    }
    return defaultVal
  }
  return validTeams.map((t: Record<string, unknown>, teamIdx: number) => {
    const teamName = (t.teamName ?? t.team_name ?? '') as string
    const members = (t.members ??
      []) as AdminPanelPageData['allianceAndSales'][0]['members']
    return {
      teamName: typeof teamName === 'string' ? teamName : '',
      members,
    }
  }) as AdminPanelPageData['allianceAndSales']
}

/** Map backend TeamMember (title, image_url, team) to app ProfileCardMemberData (role, profilePicture). */
function mapApiMemberToApp(
  row: Record<string, unknown>,
  teamIdx: number,
  i: number
): {
  id: string
  name: string
  role: string
  email?: string
  profilePicture?: string
  isHidden: boolean
} {
  const pic = row.image_url ?? row.profilePicture ?? row.profile_picture
  return {
    id: String(row.id ?? `member-${teamIdx}-${i}`),
    name: String(row.name ?? ''),
    role: String(row.title ?? row.role ?? ''),
    email: row.email != null ? String(row.email) : undefined,
    profilePicture: pic != null ? String(pic) : undefined,
    isHidden: row.isHidden === true || row.is_hidden === true,
  }
}

/** Map backend file_type to CollateralItemType. */
function mapFileTypeToCollateralType(
  fileType: unknown
): CollateralItem['type'] {
  const t = String(fileType ?? '').toLowerCase()
  if (t === 'csv') return 'csv'
  if (t === 'pdf') return 'pdf'
  if (['link', 'url', 'external_link'].includes(t)) return 'link'
  if (
    ['excel', 'xls', 'xlsx', 'spreadsheet', 'sheet'].includes(t) ||
    t === 'gsheet'
  )
    return 'spreadsheet'
  if (['document', 'doc', 'docx'].includes(t)) return 'document'
  return 'document'
}

/** Format ISO date string for display. */
function formatCollateralDate(iso: string | null | undefined): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    return isNaN(d.getTime())
      ? ''
      : d.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
  } catch {
    return ''
  }
}

/** Convert the API collateral payload → app {tabs, byTab} shape.
 *  Backend items: { id, file_url, name, file_type, uploaded_at, is_hidden, category }.
 *  category maps to tab; tab also supported. */
function normalizeCollateralFileName(name: string): string {
  const trimmed = String(name ?? '').trim()
  if (!trimmed) return trimmed
  const parts = trimmed.split('-file-')
  if (parts.length === 2 && parts[1]) {
    return parts[1]
  }
  return trimmed
}

function buildCollateralFromApi(
  raw: unknown,
  defaultCollateral: AdminPanelPageData['collateral']
): AdminPanelPageData['collateral'] {
  if (raw == null || typeof raw !== 'object') return defaultCollateral

  const items = (raw as Record<string, unknown>).items
  if (!Array.isArray(items) || items.length === 0) return defaultCollateral

  const byTab: Record<CollateralTabId, CollateralItem[]> = {
    'first-call-deck': [],
    'solution-briefs': [],
    'win-wires': [],
  }
  const categoryToTab: Record<string, CollateralTabId> = {
    'first-call-deck': 'first-call-deck',
    'first-call': 'first-call-deck',
    first_call: 'first-call-deck',
    'solution-briefs': 'solution-briefs',
    'solution-brief': 'solution-briefs',
    'win-wires': 'win-wires',
    'win-wire': 'win-wires',
  }
  const validTabs: CollateralTabId[] = [
    'first-call-deck',
    'solution-briefs',
    'win-wires',
  ]
  for (const item of items) {
    const row = item as Record<string, unknown>
    const rawCat = row.tab ?? row.category
    const rawTab =
      rawCat != null ? String(rawCat).toLowerCase().replace(/_/g, '-') : ''
    let tab = (rawTab ? categoryToTab[rawTab] : null) as CollateralTabId | null
    if (!tab || !validTabs.includes(tab)) {
      const name = String(row.name ?? '').toLowerCase()
      if (name.includes('solution brief')) tab = 'solution-briefs'
      else if (name.includes('win wire')) tab = 'win-wires'
      else tab = 'first-call-deck'
    }
    const tabId = tab as CollateralTabId
    const uploadedAtRaw = (row.uploadedAt ?? row.uploaded_at) as
      | string
      | undefined
    const collateralType = mapFileTypeToCollateralType(
      row.fileType ?? row.file_type
    )
    const mapped: CollateralItem = {
      id: String(row.id ?? ''),
      fileUrl:
        typeof (row.fileUrl ?? row.file_url) === 'string' &&
        String(row.fileUrl ?? row.file_url).trim()
          ? String(row.fileUrl ?? row.file_url).trim()
          : undefined,
      type: collateralType,
      name: normalizeCollateralFileName(String(row.name ?? '')),
      date: formatCollateralDate(uploadedAtRaw),
      action: collateralType === 'link' ? 'link' : 'download',
      isHidden: row.isHidden === true || row.is_hidden === true,
      uploadedAt: uploadedAtRaw || undefined,
    }
    if (byTab[tabId]) {
      byTab[tabId].push(mapped)
    }
  }
  return { tabs: collateralTabs, byTab }
}

/** Backend expects UUIDs for many ids. Use value if already valid UUID, else generate one. */
const UUID_REGEX =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/
function ensureUuid(id: string): string {
  return id && UUID_REGEX.test(id) ? id : uuidv4()
}

/** Convert the API solutions payload → app flat array shape.
 *  Backend may return { items: [{ solution_id, is_hidden }] } – minimal items with only solution_id and is_hidden. */
function buildSolutionsFromApi(raw: unknown): AdminPanelPageData['solutions'] {
  if (raw == null) return []
  let items: unknown[] = []
  if (Array.isArray(raw)) items = raw
  else if (typeof raw === 'object') {
    const arr = (raw as Record<string, unknown>).items
    if (Array.isArray(arr)) items = arr
  }
  return items.map((s, i) => {
    const row = (s ?? {}) as Record<string, unknown>
    const solutionId = String(row.solutionId ?? row.solution_id ?? '')
    const name = (row.name ??
      row.solution_name ??
      row.solutionId ??
      row.solution_id ??
      `Solution ${solutionId || i + 1}`) as string

    const rawSolutionType =
      row.solutionType ?? row.solution_type ?? row.solutionTypes
    const solutionType: string[] = Array.isArray(rawSolutionType)
      ? (rawSolutionType as string[])
      : typeof rawSolutionType === 'string' && rawSolutionType.trim()
        ? [rawSolutionType.trim()]
        : []

    return {
      id: row.id ?? row.solutionId ?? row.solution_id ?? `solution-${i}`,
      solutionId,
      name: String(name),
      description: String(row.description ?? ''),
      solutionType,
      isHidden: row.isHidden === true || row.is_hidden === true,
    }
  }) as AdminPanelPageData['solutions']
}

/** Map app CollateralItemType to backend file_type enum: 'pdf' | 'csv' | 'excel' | 'external_link'. */
function mapCollateralTypeToApi(type: CollateralItem['type']): string {
  switch (type) {
    case 'pdf':
      return 'pdf'
    case 'csv':
      return 'csv'
    case 'link':
      return 'external_link'
    case 'spreadsheet':
    case 'document':
    default:
      return 'excel'
  }
}

/** Convert app {tabs, byTab} → API collateral payload. Backend: file_type enum, uploaded_at ISO, category 'first_call'|'solution_brief'|'win_wire'. */
function buildApiCollateral(
  appCollateral: AdminPanelPageData['collateral'] | null
): Record<string, unknown> {
  if (!appCollateral) {
    return {
      items: [],
      first_call_section_hidden: false,
      solution_brief_section_hidden: false,
      win_wire_section_hidden: false,
      section_hidden: false,
    }
  }
  const tabToCategory: Record<string, string> = {
    'first-call-deck': 'first_call',
    'solution-briefs': 'solution_brief',
    'win-wires': 'win_wire',
  }
  const items = Object.entries(appCollateral.byTab).flatMap(([tab, tabItems]) =>
    (tabItems as CollateralItem[]).map(item => {
      const uploadedAt = item.uploadedAt ?? new Date().toISOString()
      const rawFileUrl =
        typeof item.fileUrl === 'string' ? item.fileUrl.trim() : ''
      return {
        id: ensureUuid(item.id),
        file_url: rawFileUrl ? ensureAbsoluteUrl(rawFileUrl) : null,
        name: item.name ?? '',
        file_type: mapCollateralTypeToApi(item.type ?? 'document'),
        uploaded_at: uploadedAt,
        is_hidden: item.isHidden ?? false,
        category: tabToCategory[tab] ?? 'first_call',
      }
    })
  )
  return {
    items,
    first_call_section_hidden: appCollateral.firstCallSectionHidden === true,
    solution_brief_section_hidden:
      appCollateral.solutionBriefsSectionHidden === true,
    win_wire_section_hidden: appCollateral.winWiresSectionHidden === true,
    section_hidden: appCollateral.sectionHidden === true,
  }
}

/** Convert app solutions array → API {items, section_hidden} payload. */
function buildApiSolutions(
  appSolutions: AdminPanelPageData['solutions'] | null,
  solutionsSectionHidden?: boolean
): Record<string, unknown> {
  const items = (appSolutions ?? []).map(s => ({
    id: String(s.id ?? s.solutionId ?? ''),
    solution_id: String(s.solutionId ?? s.id ?? ''),
    name: s.name ?? '',
    description: s.description ?? '',
    solution_type: Array.isArray(s.solutionType)
      ? s.solutionType.join(', ')
      : (s.solutionType ?? ''),
    is_hidden: s.isHidden === true,
  }))
  return { items, section_hidden: solutionsSectionHidden === true }
}

/** Backend Teams model: { members: [TeamMember], alliance_section_hidden, sales_section_hidden }.
 *  TeamMember: id, name, title, image_url, email, team (TeamType: alliance|sales), is_hidden. */
function buildApiTeams(
  allianceAndSales: AdminPanelPageData['allianceAndSales'],
  teamsVisibility?: AdminPanelPageData['teamsVisibility']
): Record<string, unknown> {
  const members = (allianceAndSales ?? []).flatMap(team => {
    const teamType = (team.teamName ?? '').toLowerCase().includes('alliance')
      ? 'alliance'
      : (team.teamName ?? '').toLowerCase().includes('sales')
        ? 'sales'
        : 'alliance'
    return (team.members ?? []).map(m => {
      const rawName = (m.name ?? '').trim()
      const rawRole = (m.role ?? '').trim()
      const rawEmail = (m.email ?? '').trim()
      const rawProfile = (m.profilePicture ?? '').trim()

      const title = rawRole.length >= 2 ? rawRole : null
      const email = rawEmail.length > 0 ? rawEmail : null
      const imageUrl = rawProfile ? ensureAbsoluteUrl(rawProfile) : null

      const member: Record<string, unknown> = {
        name: rawName || null,
        title,
        image_url: imageUrl,
        email,
        team: teamType,
        is_hidden: m.isHidden === true,
      }
      // Backend expects UUID ids; always send one (generate if missing).
      member.id = ensureUuid(m.id ?? '')
      return member
    })
  })
  return {
    members: members.length > 0 ? members : null,
    alliance_section_hidden: teamsVisibility?.allianceSectionHidden === true,
    sales_section_hidden: teamsVisibility?.salesSectionHidden === true,
  }
}

/** Rows with no user content (e.g. after "Add testimonial") must not be sent — API would persist empty stubs. */
export function isEmptyTestimonialDraft(t: {
  quote?: string
  name?: string
  title?: string
  companyLogo?: string
  profilePicture?: string
}): boolean {
  const quote = String(t.quote ?? '').trim()
  const name = String(t.name ?? '').trim()
  const title = String(t.title ?? '').trim()
  const companyLogo = String(t.companyLogo ?? '').trim()
  const profilePicture = String(t.profilePicture ?? '').trim()
  return (
    quote.length === 0 &&
    name.length === 0 &&
    title.length === 0 &&
    companyLogo.length === 0 &&
    profilePicture.length === 0
  )
}

function buildApiTrustedPartners(
  trustedBy: AdminPanelPageData['trustedBy']
): Record<string, unknown> {
  const userLogos = (trustedBy.customerLogos ?? []).map(c => {
    const rawLogoUrl = (c.url ?? '').trim()
    const rawWebsite = (c.website ?? '').trim()
    const customerLogoUrl = rawLogoUrl ? ensureAbsoluteUrl(rawLogoUrl) : null
    const customerWebsite = rawWebsite ? ensureAbsoluteUrl(rawWebsite) : null
    return {
      id: ensureUuid(c.id),
      customer_logo_url: customerLogoUrl,
      customer_website: customerWebsite,
      is_hidden: c.isHidden ?? false,
    }
  })
  const testimonials = (trustedBy.testimonials ?? [])
    .filter(t => !isEmptyTestimonialDraft(t))
    .map(t => {
      const profileUrl = t.profilePicture
        ? ensureAbsoluteUrl(t.profilePicture)
        : null
      const companyLogo = t.companyLogo
        ? ensureAbsoluteUrl(t.companyLogo)
        : null

      const rawReview = (t.quote ?? '').trim()
      const rawAuthorName = (t.name ?? '').trim()

      const review = rawReview.length >= 10 ? rawReview : null
      const authorName = rawAuthorName.length >= 2 ? rawAuthorName : null

      return {
        id: ensureUuid(t.id ?? ''),
        review,
        author_name: authorName,
        designation: t.title ?? null,
        author_logo_url: profileUrl,
        author_company_url: companyLogo,
        is_hidden: t.isHidden ?? false,
      }
    })
  return {
    user_logos: userLogos,
    testimonials,
    section_hidden: trustedBy.sectionHidden === true,
  }
}

/** Build highlights for API. Backend expects highlight_logo_url, partner_id, information_badges, section_hidden. */
function buildApiHighlights(
  highlightMetrics: AdminPanelPageData['highlightMetrics']
): Record<string, unknown> {
  const informationBadges = [
    ...(highlightMetrics.awsMetrics ?? []).map(m => {
      const rawLabel = (m.label ?? '').trim()
      const rawValue = (m.value ?? '').trim()
      return {
        id: ensureUuid(m.id),
        label: rawLabel || null,
        title: rawLabel || null,
        value: rawValue || null,
        description: m.description ?? '',
        is_hidden: m.isVisible === false,
      }
    }),
    ...(highlightMetrics.informationBadges ?? []).map(b => {
      const rawLabel = (b.label ?? '').trim()
      const rawValue = (b.value ?? '').trim()
      return {
        id: ensureUuid(b.id),
        label: rawLabel || null,
        title: rawLabel || null,
        value: rawValue || null,
        description: b.description ?? '',
        is_hidden: b.isVisible === false,
      }
    }),
  ]
  return {
    highlight_logo_url:
      (highlightMetrics.recognitionBadgeFileLinks ?? [])[0] ?? null,
    partner_id: (highlightMetrics.partnerId ?? '').trim() || null,
    information_badges: informationBadges,
    section_hidden: highlightMetrics.sectionHidden === true,
  }
}

/** Build company details for API. Backend expects company_name, company_description, linkedin_url, website, info, section_hidden (boolean). */
function buildApiCompanyDetails(
  companyInfo: AdminPanelPageData['companyInfo']
): Record<string, unknown> {
  const info =
    Array.isArray(companyInfo.stats) && companyInfo.stats.length > 0
      ? companyInfo.stats.map(s => {
          const title = (s.label ?? '').trim()
          return {
            title: title || null,
            label: title || null,
            name: title || null,
            value: s.value ?? null,
            is_hidden: s.isVisible === false,
          }
        })
      : null
  return {
    company_name: (companyInfo.companyName ?? '').trim() || null,
    company_description: (companyInfo.description ?? '').trim() || null,
    company_logo: companyInfo.companyLogo ?? null,
    linkedin_url: ensureAbsoluteUrl(companyInfo.linkedinUrl),
    website: ensureAbsoluteUrl(companyInfo.websiteUrl),
    info,
    section_hidden: companyInfo.sectionHidden === true,
  }
}

/** Build the full API payload from app state, mapping all field names and structures. */
function buildPartnerInfoPayload(
  partnerId: string,
  pageData: AdminPanelPageData,
  currentUser?: { id?: string | null; email?: string | null }
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    [COLLAB_ROOM_API_KEYS.partnerId]: partnerId,
    [COLLAB_ROOM_API_KEYS.companyDetails]: buildApiCompanyDetails(
      pageData.companyInfo
    ),
    [COLLAB_ROOM_API_KEYS.trustedPartners]: buildApiTrustedPartners(
      pageData.trustedBy
    ),
    [COLLAB_ROOM_API_KEYS.highlights]: buildApiHighlights(
      pageData.highlightMetrics
    ),
    [COLLAB_ROOM_API_KEYS.teams]: buildApiTeams(
      pageData.allianceAndSales,
      pageData.teamsVisibility
    ),
    [COLLAB_ROOM_API_KEYS.collateral]: buildApiCollateral(pageData.collateral),
    [COLLAB_ROOM_API_KEYS.solutions]: buildApiSolutions(
      pageData.solutions,
      pageData.solutionsSectionHidden
    ),
  }

  if (!payload[COLLAB_ROOM_API_KEYS.createdBy] && currentUser) {
    payload[COLLAB_ROOM_API_KEYS.createdBy] =
      currentUser.id || currentUser.email || partnerId
  }

  return payload
}

export const saveCollabRoomPageDataThunk =
  (partnerId: string, dataOverride?: AdminPanelPageData | null) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const pageData = dataOverride ?? getState().partnerIQ.adminPanelPageData
    if (!pageData) {
      dispatch(
        updateAppAlert({
          message: 'No data to save',
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      return
    }
    await dispatch(
      startLoading(LoadingTypes.SAVE_PARTNER_IQ_COLLAB_ROOM_PAGE_DATA)
    )
    const globalState = getState()
    const currentUser = globalState.userProfile?.userProfile
    const payload = buildPartnerInfoPayload(partnerId, pageData, {
      id: currentUser?.id,
      email: currentUser?.email,
    })
    try {
      try {
        await saveCollabRoomPageData(partnerId, payload)
      } catch (putError: unknown) {
        const putResponse = (putError as AxiosError)?.response as
          | AxiosResponse<{ code?: string }>
          | undefined
        if (
          putResponse?.status === 404 ||
          putResponse?.data?.code === 'ERR_NOT_FOUND'
        ) {
          await createCollabRoomPageData(partnerId, payload)
        } else {
          throw putError
        }
      }
      dispatch(
        updateAppAlert({
          message: 'Collab Room saved successfully.',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      // Refetch so Redux (and Collab Room) shows the latest from API.
      // Pass fallbacks when the API omits or returns empty slices we just saved (common for solutions.items).
      await dispatch(
        getCollabRoomPageData(partnerId, {
          fallbackAllianceAndSales: pageData.allianceAndSales,
          fallbackSolutions: pageData.solutions,
          fallbackSolutionsSectionHidden:
            pageData.solutionsSectionHidden === true,
        })
      )
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(
        stopLoading(LoadingTypes.SAVE_PARTNER_IQ_COLLAB_ROOM_PAGE_DATA)
      )
    }
  }

export const getPartnerIqTableData =
  (
    partnerId: string,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc',
    segment?: string,
    icpFitMin?: number,
    icpFitMax?: number,
    search?: string,
    searchType?: string,
    append?: boolean
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_PARTNER_IQ_TABLE_DATA))
    try {
      let title: string | undefined
      let contactName: string | undefined
      let location: string | undefined

      if (search) {
        const normalizedSearchType = searchType?.toLowerCase() || 'contact name'
        if (normalizedSearchType === 'title') {
          title = search
        } else if (normalizedSearchType === 'location') {
          location = search
        } else {
          contactName = search
        }
      }

      const response = await fetchPartnerIqTableData(
        partnerId,
        sortBy,
        sortOrder,
        segment,
        icpFitMin,
        icpFitMax,
        title,
        contactName,
        location
      )
      if (!isEmpty(response?.data)) {
        const camelizedData = camelize(response?.data) as PartnerIqTableData
        if (append) {
          const existingTableData = getState().partnerIQ.tableData
          const existingContacts = existingTableData.contacts || []
          camelizedData.contacts = [
            ...existingContacts,
            ...(camelizedData.contacts || []),
          ]
          camelizedData.totalCount = existingTableData.totalCount
        }
        await dispatch(setPartnerIqTableData(camelizedData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_PARTNER_IQ_TABLE_DATA))
    }
  }

const transformPartnerIqOverviewData = (
  data: PartnerIqOverviewResponse
): OverviewComponentData[] => {
  const overviewData: OverviewComponentData[] = []

  // Transform top_performers_segments into "Top performing segments" overview
  if (data.topPerformersSegments) {
    const segmentsArray = data.topPerformersSegments
    const validSegments = segmentsArray
      .filter(item => item !== null && item !== undefined)
      .map(item => {
        return typeof item === 'string' ? item : item.value
      })

    if (validSegments.length > 0) {
      overviewData.push({
        title: 'Top performing segments',
        type: validSegments.map(segment => ({
          title: segment,
        })),
      })
    }
  }

  // Transform top_performers_locations into "Top performing locations" overview
  if (data.topPerformersLocations) {
    const locationsArray = data.topPerformersLocations
    const validLocations = locationsArray
      .filter(item => item !== null && item !== undefined)
      .map(item => {
        return typeof item === 'string' ? item : item.value
      })

    if (validLocations.length > 0) {
      overviewData.push({
        title: 'Top performing locations',
        type: validLocations.map(location => ({
          title: location,
        })),
      })
    }
  }

  // Transform top_performers into "Top performer" overview
  if (data.topPerformers?.length) {
    overviewData.push({
      title: 'Top performer',
      type: data.topPerformers.map(performer => ({
        title: performer.contactName,
        contactId: performer.contactId,
        segment: performer.segment,
        location: performer.location,
      })),
    })
  }

  // Transform opportunities into "Opportunities" overview
  if (data.opportunities) {
    const { inProgress, closedWon, closedLost } = data.opportunities
    const opportunityItems: Array<{ title: string; value?: number }> = []

    if (inProgress) {
      opportunityItems.push({
        title: `${inProgress.count} In progress`,
        value: inProgress.value,
      })
    }

    if (closedWon) {
      opportunityItems.push({
        title: `${closedWon.count} Closed won`,
        value: closedWon.value,
      })
    }

    if (closedLost) {
      opportunityItems.push({
        title: `${closedLost.count} Closed lost`,
        value: closedLost.value,
      })
    }

    if (opportunityItems.length > 0) {
      overviewData.push({
        title: 'Opportunities',
        type: opportunityItems,
      })
    }
  }

  return overviewData
}

export const getPartnerIqSegments =
  (partnerId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_PARTNER_IQ_SEGMENTS_DATA))
    try {
      const response = await fetchPartnerIqSegments(partnerId)
      if (!isEmpty(response?.data)) {
        const camelizedData = camelize(response?.data) as SegmentsData
        await dispatch(setPartnerIqSegmentsData(camelizedData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_PARTNER_IQ_SEGMENTS_DATA))
    }
  }

export const getPartnerIqOverviewData =
  (partnerId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_PARTNER_IQ_TABLE_DATA))
    try {
      const response = await fetchPartnerIqOverviewData(partnerId)
      if (!isEmpty(response?.data)) {
        const camelizedData = camelize(
          response?.data
        ) as PartnerIqOverviewResponse
        const overviewData = transformPartnerIqOverviewData(camelizedData)
        await dispatch(setPartnerIqOverviewData(overviewData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_PARTNER_IQ_TABLE_DATA))
    }
  }

export const getPartnerIqIcpFormData =
  (partnerId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_PARTNER_IQ_ICP_FORM_DATA))
    try {
      const response = await fetchPartnerIqIcpFormData(partnerId)
      if (!isEmpty(response?.data)) {
        const camelizedData = camelize(response?.data) as unknown as {
          id?: string
          partnerId?: string
          name?: string
          annualRevenue?: string | null | string[]
          industry?: string[]
          employeeSize?: string[]
          segment?: string[]
          cloudTechnologyStack?: string[]
          geographicalRegion?: string[]
          marketplacePresence?: string[]
          userId?: string
        }

        // Transform the response to match ICPData format
        const transformedData: ICPData = {
          id: camelizedData.id,
          partnerId: camelizedData.partnerId,
          icpName: camelizedData.name || '',
          // Handle annualRevenue: API returns string | null, but we need string[]
          annualRevenue:
            camelizedData.annualRevenue === null ||
            camelizedData.annualRevenue === undefined
              ? []
              : Array.isArray(camelizedData.annualRevenue)
                ? camelizedData.annualRevenue
                : [camelizedData.annualRevenue],
          industry: camelizedData.industry || [],
          employeeSize: camelizedData.employeeSize || [],
          segment: camelizedData.segment || [],
          cloudTechnologyStack: camelizedData.cloudTechnologyStack || [],
          geographicalRegion: camelizedData.geographicalRegion || [],
          marketplacePresence: camelizedData.marketplacePresence || [],
          userId: camelizedData.userId || '',
        }

        await dispatch(setPartnerIqIcpFormData(transformedData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_PARTNER_IQ_ICP_FORM_DATA))
    }
  }

export const getPartnerIqProfileData =
  (partnerId: string, contactId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_PARTNER_IQ_PROFILE_DATA))
    try {
      const response = await fetchPartnerIqProfileData(partnerId, contactId)
      if (!isEmpty(response?.data)) {
        const camelizedData = camelize(response?.data) as ProfileData
        await dispatch(setPartnerIqProfileData(camelizedData))
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_PARTNER_IQ_PROFILE_DATA))
    }
  }

export const updatePartnerIqIcpFormData =
  (partnerId: string, data: ICPData) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.UPDATE_PARTNER_IQ_ICP_FORM_DATA))
    try {
      const state = getState()
      const userId = data.userId || state.userProfile?.userProfile?.id

      const apiData = {
        name: data.icpName,
        annualRevenue: data.annualRevenue || [],
        industry: data.industry || [],
        employeeSize: data.employeeSize || [],
        segment: data.segment || [],
        cloudTechnologyStack: data.cloudTechnologyStack || [],
        geographicalRegion: data.geographicalRegion || [],
        marketplacePresence: data.marketplacePresence || [],
        userId: userId,
        ...(data.id && { id: data.id }),
      }

      const response = await putPartnerIqIcpFormData(partnerId, apiData)
      if (!isEmpty(response?.data)) {
        const camelizedData = camelize(response?.data) as ICPData
        const transformedData: ICPData = {
          ...camelizedData,
          icpName: (camelizedData as unknown as { name?: string })?.name || '',
        }
        await dispatch(setPartnerIqIcpFormData(transformedData))
        await dispatch(
          updateAppAlert({
            message: transformedData.icpName + ' ' + ICPUpdateSuccessMessage,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.UPDATE_PARTNER_IQ_ICP_FORM_DATA))
    }
  }

export const getPartnerIqTableReportFile =
  (
    partnerId: string,
    year: string,
    page?: number,
    pageSize?: number,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc',
    segment?: string,
    icpFitMin?: number,
    icpFitMax?: number,
    contactName?: string,
    title?: string,
    location?: string
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.FETCH_PARTNER_IQ_TABLE_REPORT))

    try {
      const response = await fetchPartnerIqTableReport(
        partnerId,
        page,
        pageSize,
        sortBy,
        sortOrder,
        segment,
        icpFitMin,
        icpFitMax,
        contactName,
        title,
        location
      )

      const filename = `contacts_report_${year}.csv`
      FileDownload(response.data, filename)
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      throw error
    } finally {
      await dispatch(stopLoading(LoadingTypes.FETCH_PARTNER_IQ_TABLE_REPORT))
    }
  }

export const getPartnerIqOpportunitiesReportFile =
  (partnerId: string, contactId: string, year: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(
      startLoading(LoadingTypes.FETCH_PARTNER_IQ_OPPORTUNITIES_REPORT)
    )

    try {
      const response = await fetchPartnerIqOpportunitiesReport(
        partnerId,
        contactId
      )

      const filename = `opportunities_report_${year}.csv`
      FileDownload(response.data, filename)
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      throw error
    } finally {
      await dispatch(
        stopLoading(LoadingTypes.FETCH_PARTNER_IQ_OPPORTUNITIES_REPORT)
      )
    }
  }
